/**
 * The marketing site's footer, on the invite.
 *
 * Ported rather than imported: foundlio.com is Next with `next/link`, this app
 * is Vite with React Router, and there is no shared component package. Keep the
 * link lists in step with `apps/website/components/layout/Footer.tsx` by hand.
 *
 * Two deliberate differences from the original:
 *
 *   1. Every link opens in a new tab. Someone reading about pricing should not
 *      have to find their way back to their own website afterwards — it stays
 *      open behind them.
 *   2. Every link carries `?ref=<slug>`, which lets the marketing site recognise
 *      them and put their website back in front of them. The slug is public (it
 *      is the site's own subdomain); the invite token stays here, because it is
 *      a credential.
 */

import React from 'react';

const MARKETING_URL = (
  import.meta.env.VITE_MARKETING_URL || 'https://foundlio.com'
).replace(/\/$/, '');

const FOOTER_LINKS = {
  company: [
    { name: 'Home', href: '/' },
    { name: 'Features', href: '/features' },
    { name: 'Demos', href: '/demos' },
    { name: 'Why Us', href: '/why-us' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Contact', href: '/contact' },
  ],
  industries: [
    { name: 'Plumbers', href: '/for/plumbers' },
    { name: 'Roofers', href: '/for/roofers' },
    { name: 'HVAC', href: '/for/hvac' },
    { name: 'Electricians', href: '/for/electricians' },
    { name: 'Landscapers', href: '/for/landscapers' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Refunds & cancellation', href: '/terms#refunds' },
  ],
};

const linkClass =
  'inline-block rounded-sm text-sm text-zinc-300 underline-offset-4 transition-colors hover:text-white hover:underline';

export default function MarketingFooter({
  slug,
  supportEmail,
}: {
  /** Site slug, from the preview domain. Absent for a preview with no domain yet. */
  slug?: string;
  supportEmail: string;
}) {
  const withRef = (href: string) => {
    const url = `${MARKETING_URL}${href}`;
    if (!slug) return url;
    return `${url}${href.includes('?') ? '&' : '?'}ref=${encodeURIComponent(slug)}`;
  };

  const renderLinks = (links: { name: string; href: string }[]) =>
    links.map(link => (
      <li key={link.name}>
        <a
          href={withRef(link.href)}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          {link.name}
        </a>
      </li>
    ));

  return (
    // The claim bar is fixed to the bottom and is always up by the time anyone
    // reaches the footer, so the last row needs to clear it at every width.
    <footer className="border-t border-white/10 bg-[#1e2228] pb-28 text-white sm:pb-24">
      <div className="mx-auto max-w-6xl px-5 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4 md:gap-12">
          <div className="min-w-0 md:col-span-2">
            <a
              href={withRef('/')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5"
            >
              <img src="/foundlio-on-dark.svg" alt="Foundlio" className="h-9 w-auto" />
              <span className="font-display text-lg font-bold tracking-[-0.03em] text-white">
                Foundlio
              </span>
            </a>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-zinc-400">
              Professional websites for contractors. Plumbers, roofers, HVAC,
              electricians, landscapers — we get you online and growing.
            </p>
            <p className="mt-5 text-[15px] leading-relaxed text-zinc-400">
              Questions?{' '}
              <a
                href={`mailto:${supportEmail}`}
                className="text-zinc-200 underline decoration-white/25 underline-offset-4 hover:text-white"
              >
                {supportEmail}
              </a>
            </p>
          </div>

          <div className="min-w-0">
            <h3 className="mb-5 border-b border-white/10 pb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">
              Company
            </h3>
            <ul className="space-y-3">{renderLinks(FOOTER_LINKS.company)}</ul>
          </div>

          <div className="min-w-0">
            <h3 className="mb-5 border-b border-white/10 pb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">
              Industries
            </h3>
            <ul className="space-y-3">{renderLinks(FOOTER_LINKS.industries)}</ul>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
              {new Date().getFullYear()} Foundlio. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              {FOOTER_LINKS.legal.map(link => (
                <a
                  key={link.name}
                  href={withRef(link.href)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-sm text-sm text-zinc-400 underline-offset-4 transition-colors hover:text-white hover:underline"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
